import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import {
  Header,
  Modal,
  Button,
  Label,
  Input,
  FieldGroup,
  Fields,
  Text,
  Loader,
} from '@pet-notify/ui'

import { useLookupOrder } from 'hooks/orders'
import type { LookupOrderParams } from 'services/orders'
import ErrorMessage from 'components/ErrorMessage'
import ButtonGroup from 'components/ButtonGroup'

type Props = {
  onClose: () => void
}

function LookupCodeModal({ onClose }: Props) {
  const { register, handleSubmit } = useForm<LookupOrderParams>()
  const lookupOrderByUserMutation = useLookupOrder()
  const lookupOrderByOrderNumberMutation = useLookupOrder()

  const order = useMemo(() => {
    return (
      lookupOrderByUserMutation.data || lookupOrderByOrderNumberMutation.data
    )
  }, [lookupOrderByUserMutation.data, lookupOrderByOrderNumberMutation.data])

  return (
    <Modal onClose={onClose}>
      {order ? (
        <>
          <Header as='h3' size='xlarge' mb='6'>
            Order found!
          </Header>

          <Text mb='6'>
            Your order activation code is{' '}
            <strong>{order.activationCode}</strong>.
          </Text>

          <Button type='button' onClick={onClose}>
            Activate your order
          </Button>
        </>
      ) : (
        <>
          <Header as='h3' size='xlarge' mb='6'>
            Lookup your activation code
          </Header>
          <form
            onSubmit={handleSubmit((data) => {
              lookupOrderByUserMutation.mutate(data)
            })}
          >
            <Fields>
              <>
                <FieldGroup>
                  <Label htmlFor='firstName'>First Name</Label>
                  <Input
                    type='text'
                    id='firstName'
                    {...register('firstName')}
                  />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor='lastName'>Last Name</Label>
                  <Input type='text' id='lastName' {...register('lastName')} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor='phone'>Mobile number</Label>
                  <Input type='text' id='phone' {...register('phone')} />
                </FieldGroup>
                {lookupOrderByUserMutation.error && (
                  <ErrorMessage error={lookupOrderByUserMutation.error} />
                )}
              </>
            </Fields>
            <ButtonGroup mb='6'>
              {lookupOrderByUserMutation.isLoading ? (
                <Loader />
              ) : (
                <Button type='submit'>Lookup activation code</Button>
              )}
            </ButtonGroup>
          </form>

          <Header as='h3' size='medium' mb='6'>
            Or lookup your activation code by order number
          </Header>

          <form
            onSubmit={handleSubmit((data) => {
              lookupOrderByOrderNumberMutation.mutate(data)
            })}
          >
            <Fields>
              <>
                <FieldGroup>
                  <Label htmlFor='orderNumber'>Order number</Label>
                  <Input
                    type='text'
                    id='orderNumber'
                    {...register('orderNumber')}
                  />
                </FieldGroup>
                {lookupOrderByOrderNumberMutation.error && (
                  <ErrorMessage
                    error={lookupOrderByOrderNumberMutation.error}
                  />
                )}
              </>
            </Fields>
            {lookupOrderByOrderNumberMutation.isLoading ? (
              <Loader />
            ) : (
              <Button type='submit'>
                Lookup activation code by order number
              </Button>
            )}
          </form>
        </>
      )}
    </Modal>
  )
}

export default LookupCodeModal
